import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Sparkles } from "lucide-react";
import { useLang } from "@/i18n";
import { scrollToId } from "@/lib/site";
import { ShineButton } from "./ShineButton";

const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay, ease }}>
      {children}
    </motion.span>
  </span>
);

const HeroVisual = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} style={{ perspective: 1200 }} className="relative w-full max-w-[460px] mx-auto">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[2rem] border-[6px] border-jungle bg-jungle shadow-[0_50px_80px_-40px_rgba(8,26,19,0.7)]">
          <img src="/img/hero-local.jpg" alt="Interior de El gato con copas con neón 'Que el fin del mundo nos pille bailando' y pared de plantas tropicales" className="h-full w-full object-cover scale-110" data-testid="hero-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-deep/30 via-transparent to-transparent" />
        </div>
        <motion.img
          src="/sello-gato-sm.jpg"
          alt="Sello El gato con copas"
          style={{ translateZ: 80 }}
          className="absolute -left-6 sm:-left-10 bottom-10 w-32 sm:w-40 rounded-2xl border-4 border-paper drop-shadow-[0_20px_30px_rgba(8,26,19,0.45)]"
          initial={{ scale: 0, rotate: -40 }}
          animate={{ scale: 1, rotate: -10 }}
          transition={{ delay: 1.2, type: "spring", stiffness: 140, damping: 12 }}
        />
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} id="inicio" data-testid="hero-section" className="relative overflow-hidden bg-tropical-light pt-28 sm:pt-32 pb-16 lg:pb-24 min-h-[100svh]">

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}
            className="inline-flex items-center gap-2 rounded-full border border-jungle/15 bg-paper/70 backdrop-blur px-4 py-1.5" data-testid="hero-eyebrow">
            <Sparkles size={13} className="text-mango" />
            <span className="font-mono-ed text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.28em]">{t.hero.eyebrow}</span>
          </motion.div>
          <h1 className="font-brand mt-6 text-[clamp(3.2rem,10vw,7.5rem)] [&>span>span]:whitespace-nowrap leading-[0.88] tracking-tight text-gato" data-testid="hero-title">
            <MaskLine delay={0.15}>{t.hero.l1}</MaskLine>
            <MaskLine delay={0.32} className="pl-[0.6em]">{t.hero.l2}</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8, ease }}
            className="mt-6 font-display italic text-2xl sm:text-3xl text-berenjena" data-testid="hero-claim">
            {t.hero.claim}
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8, ease }}
            className="mt-5 max-w-lg text-base sm:text-lg leading-relaxed text-jungle/80" data-testid="hero-subtitle">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8, ease }} className="mt-8 flex flex-wrap items-center gap-4">
            <ShineButton testId="hero-cta" onClick={() => scrollToId("contacto")}>{t.hero.cta}</ShineButton>
            <button data-testid="hero-carta-link" onClick={() => scrollToId("carta")} className="text-[12px] font-bold uppercase tracking-[0.14em] border-b-2 border-jungle pb-1 hover:text-mango hover:border-mango transition-colors">
              {t.hero.cta2}
            </button>
          </motion.div>
        </motion.div>
        <motion.div className="lg:col-span-5 relative" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 1.2, ease }}>
          <HeroVisual />
          <div className="absolute -top-4 right-2 sm:right-6 h-32 w-32 rounded-full bg-flamingo text-paper flex items-center justify-center text-center rotate-12 shadow-[0_0_30px_rgba(238,100,64,0.55)] ring-4 ring-paper/70" data-testid="hero-badge">
            <span className="font-neon text-[22px] leading-[1.05] px-3 [text-shadow:0_0_6px_rgba(255,255,255,0.8),0_0_14px_rgba(255,220,230,0.6)]">{t.hero.badge}</span>
          </div>
        </motion.div>
      </div>
      <button onClick={() => scrollToId("historia")} aria-label="Bajar" data-testid="hero-scroll-hint" className="hidden sm:block absolute bottom-6 left-1/2 -translate-x-1/2 text-jungle/60 hover:text-mango">
        <ArrowDown className="animate-bounce" size={20} />
      </button>
    </section>
  );
};
